import { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  Button as MuiButton,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
} from '@mui/material';
import { CheckCircleOutline, DeleteForever, Language, SystemUpdate } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Snackbar } from '@components/common/Snackbar';
import { db } from '@database/db';
import { seedDatabase } from '@database/seed';
import { logger } from '@utils/logger';
import { useSwUpdateStore } from '@store/sw-update-store';

export function SettingsPage() {
  const { t, i18n } = useTranslation();
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const { updateAvailable, updateSW } = useSwUpdateStore();

  const handleClearData = async () => {
    const confirmed = window.confirm(t('settings.clearConfirm'));

    if (confirmed) {
      try {
        await db.delete();
        await db.open();
        localStorage.clear();

        // Re-seed the database with HCW categories
        await seedDatabase();
        logger.info('Database re-seeded after data clear');

        setSuccess(t('settings.clearSuccess'));
      } catch (err) {
        logger.error('Failed to clear data:', err);
        setError(t('settings.clearError'));
      }
    }
  };

  const handleLanguageChange = (
    _event: React.MouseEvent<HTMLElement>,
    newLang: string | null
  ) => {
    if (newLang) {
      i18n.changeLanguage(newLang);
    }
  };

  const handleUpdate = async () => {
    if (updateSW) {
      setIsUpdating(true);
      await updateSW();
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('settings.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('settings.subtitle')}
        </Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          <Language sx={{ verticalAlign: 'middle', mr: 1 }} />
          {t('settings.language')}
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('settings.languageDescription')}
        </Typography>
        <ToggleButtonGroup
          value={i18n.language.startsWith('es') ? 'es' : 'en'}
          exclusive
          onChange={handleLanguageChange}
          aria-label={t('settings.language')}
        >
          <ToggleButton value="es">
            {t('settings.spanish')}
          </ToggleButton>
          <ToggleButton value="en">
            {t('settings.english')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('settings.dataManagement')}
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('settings.clearDataDescription')}
        </Typography>
        <MuiButton
          variant="outlined"
          color="error"
          startIcon={<DeleteForever />}
          onClick={handleClearData}
        >
          {t('settings.clearAllData')}
        </MuiButton>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          <SystemUpdate sx={{ verticalAlign: 'middle', mr: 1 }} />
          {t('settings.appVersionAndUpdates')}
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>{t('settings.version')}:</strong> {__APP_VERSION__}
        </Typography>
        {updateAvailable ? (
          <MuiButton
            variant="contained"
            color="primary"
            startIcon={<SystemUpdate />}
            onClick={handleUpdate}
            disabled={isUpdating}
          >
            {isUpdating ? t('settings.updating') : t('settings.updateNow')}
          </MuiButton>
        ) : (
          <Chip
            icon={<CheckCircleOutline />}
            label={t('settings.upToDate')}
            color="success"
            variant="outlined"
          />
        )}
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('settings.appInfo')}
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2">
            <strong>{t('settings.platform')}:</strong> {t('settings.platformValue')}
          </Typography>
          <Typography variant="body2">
            <strong>{t('settings.storage')}:</strong> {t('settings.storageValue')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {t('settings.appDescription')}
          </Typography>
        </Box>
      </Paper>

      <Snackbar
        open={!!error}
        message={error || ''}
        severity="error"
        onClose={() => setError(null)}
      />
      <Snackbar
        open={!!success}
        message={success || ''}
        severity="success"
        onClose={() => setSuccess(null)}
      />
    </Container>
  );
}
